/**
 * A field issue's offending value as it is stored, turned into something a
 * reviewer can read. The two writers do not agree on a format: most rows hold
 * comma-joined text, a few hold a JSON array, so `["TX1"]` and `TX1` both mean
 * the same one code. Every surface that shows these values reads them through
 * here, so a row and its expander can never disagree about what the value is.
 */
export function parseStoredFieldIssueValues(value: string | null | undefined): string[] {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "[]") return [];

  // Comma-joined text is the default reading; a row that parses as JSON takes
  // the parsed shape instead.
  let parts = trimmed.split(",");
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) parts = parsed.map((item) => String(item));
    else if (typeof parsed === "string") parts = [parsed];
  } catch {
    // Bare codes are not valid JSON; the comma split already covers them.
  }

  return parts.map((item) => item.trim()).filter((item) => item.length > 0);
}

/** One source's stored value as display text, or null when that source holds none. */
export function formatStoredFieldIssueValue(value: string | null | undefined): string | null {
  const values = parseStoredFieldIssueValues(value);
  return values.length > 0 ? values.join(", ") : null;
}

/** Every distinct value across a field issue's three sources, or a dash when it has none. */
export function getFieldIssueValueText(d: {
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
}): string {
  const uniqueValues = new Set<string>();
  for (const value of [d.eManifestValue, d.vendorValue, d.manifestDocumentValue]) {
    for (const parsedValue of parseStoredFieldIssueValues(value)) {
      uniqueValues.add(parsedValue);
    }
  }
  return uniqueValues.size > 0 ? [...uniqueValues].join(", ") : "—";
}
